import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { peopleCoreAPI } from '@/shared/lib/api';
import type { Employee, Department, JobPosition, SalaryHistory, PerformanceInsight, HRDashboardData } from '../types';

// ==========================================
// EMPLOYEES HCM
// ==========================================

export function useEmployeesHCM(params?: { status?: string; search?: string; departmentId?: string; jobPositionId?: string }) {
  return useQuery<Employee[]>({
    queryKey: ['people-core', 'hcm', 'employees', params],
    queryFn: async () => {
      const res = await peopleCoreAPI.listEmployeesHCM(params);
      return res.data.data ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useEmployeeHCM(id: string | undefined) {
  return useQuery<Employee>({
    queryKey: ['people-core', 'hcm', 'employees', id],
    queryFn: async () => {
      if (!id) throw new Error('Employee ID is required');
      const res = await peopleCoreAPI.getEmployeeHCM(id);
      return res.data.data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateEmployee() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: unknown) => {
      const res = await peopleCoreAPI.createEmployeeHCM(data);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['people-core', 'hcm', 'employees'] });
      queryClient.invalidateQueries({ queryKey: ['people-core', 'hcm', 'dashboard'] });
    },
  });
}

export function useUpdateEmployee() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: unknown }) => {
      const res = await peopleCoreAPI.updateEmployeeHCM(id, data);
      return res.data.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['people-core', 'hcm', 'employees'] });
      queryClient.invalidateQueries({ queryKey: ['people-core', 'hcm', 'employees', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['people-core', 'hcm', 'dashboard'] });
    },
  });
}

export function useDeleteEmployee() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await peopleCoreAPI.deleteEmployeeHCM(id);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['people-core', 'hcm', 'employees'] });
      queryClient.invalidateQueries({ queryKey: ['people-core', 'hcm', 'dashboard'] });
    },
  });
}

// ==========================================
// DEPARTMENTS
// ==========================================

export function useDepartments(includeInactive = false) {
  return useQuery<Department[]>({
    queryKey: ['people-core', 'hcm', 'departments', { includeInactive }],
    queryFn: async () => {
      const res = await peopleCoreAPI.listDepartments({ includeInactive });
      return res.data.data ?? [];
    },
    staleTime: 10 * 60 * 1000,
  });
}

export function useCreateDepartment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { name: string; description?: string }) => {
      const res = await peopleCoreAPI.createDepartment(data);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['people-core', 'hcm', 'departments'] });
    },
  });
}

// ==========================================
// JOB POSITIONS
// ==========================================

export function useJobPositions(params?: { departmentId?: string; includeInactive?: boolean }) {
  return useQuery<JobPosition[]>({
    queryKey: ['people-core', 'hcm', 'job-positions', params],
    queryFn: async () => {
      const res = await peopleCoreAPI.listJobPositions(params);
      return res.data.data ?? [];
    },
    staleTime: 10 * 60 * 1000,
  });
}

export function useCreateJobPosition() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { name: string; departmentId: string; baseSalary?: number }) => {
      const res = await peopleCoreAPI.createJobPosition(data);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['people-core', 'hcm', 'job-positions'] });
    },
  });
}

// ==========================================
// SALARY HISTORY
// ==========================================

export function useSalaryHistory(employeeId: string | undefined) {
  return useQuery<SalaryHistory[]>({
    queryKey: ['people-core', 'hcm', 'salary-history', employeeId],
    queryFn: async () => {
      if (!employeeId) throw new Error('Employee ID is required');
      const res = await peopleCoreAPI.getSalaryHistory(employeeId);
      return res.data.data ?? [];
    },
    enabled: !!employeeId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useAddSalaryHistory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ employeeId, data }: {
      employeeId: string;
      data: {
        value: number;
        employeeSalary?: number;
        reason?: string;
      }
    }) => {
      const res = await peopleCoreAPI.addSalaryHistory(employeeId, data);
      return res.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['people-core', 'hcm', 'salary-history', variables.employeeId] });
      queryClient.invalidateQueries({ queryKey: ['people-core', 'hcm', 'employees', variables.employeeId] });
    },
  });
}

// ==========================================
// PERFORMANCE INSIGHTS
// ==========================================

export function usePerformanceInsights(employeeId: string | undefined) {
  return useQuery<PerformanceInsight[]>({
    queryKey: ['people-core', 'hcm', 'performance-insights', employeeId],
    queryFn: async () => {
      if (!employeeId) throw new Error('Employee ID is required');
      const res = await peopleCoreAPI.getPerformanceInsights(employeeId);
      return res.data.data ?? [];
    },
    enabled: !!employeeId,
    staleTime: 5 * 60 * 1000,
  });
}

// ==========================================
// HR DASHBOARD
// ==========================================

export function useHRDashboard() {
  return useQuery<HRDashboardData>({
    queryKey: ['people-core', 'hcm', 'dashboard'],
    queryFn: async () => {
      const res = await peopleCoreAPI.getHRDashboard();
      return res.data.data;
    },
    staleTime: 5 * 60 * 1000,
  });
}
